import { Link } from 'react-router-dom';
import type { GossipTopic } from '@/types';

interface TopicCardProps {
  topic: GossipTopic;
  rank?: number;
}

function formatHeat(value: number) {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return `${value}`;
}

export default function TopicCard({ topic, rank }: TopicCardProps) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/topic/${topic.id}`} className="relative block h-[168px] w-full overflow-hidden md:h-[188px]">
        <img
          src={topic.cover}
          alt={`${topic.title} 相关配图`}
          title={`${topic.category} · ${topic.title}`}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 flex items-center gap-1 rounded-md bg-primary-500/95 px-2.5 py-1 text-xs font-semibold text-background-50">
          <i className="ri-price-tag-3-fill" aria-hidden="true" />
          {topic.category}
        </span>
        {rank !== undefined && (
          <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md bg-secondary-950/85 font-heading text-sm font-black text-accent-400">
            {rank}
          </span>
        )}
        <span className="absolute bottom-3 left-3 rounded-md bg-secondary-950/80 px-2 py-0.5 text-[11px] text-background-100">
          {topic.credibility}
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <span className="text-xs font-medium text-secondary-700">{topic.mask}</span>
        <h3 className="mt-2 line-clamp-2-custom font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-[17px]">
          <Link to={`/topic/${topic.id}`}>{topic.title}</Link>
        </h3>
        <p className="mt-2 line-clamp-2-custom text-[13px] leading-relaxed text-foreground-600">{topic.summary}</p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {topic.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-accent-100 px-2.5 py-0.5 text-[11px] font-medium text-accent-900"
            >
              #{tag}
            </span>
          ))}
        </div>

        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              {formatHeat(topic.heat)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true" />
              {topic.comments}
            </span>
          </span>
          <span>{topic.publishedAt}</span>
        </div>
      </div>
    </article>
  );
}